import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@material-ui/core";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import { toast } from "react-toastify";

import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { phoneDigits } from "../../utils/phoneNumbers";

const StartConversationFromNumberModal = ({ open, onClose, initialNumber }) => {
  const history = useHistory();
  const { whatsApps = [] } = useContext(WhatsAppsContext);
  const [number, setNumber] = useState("");
  const [countryCode, setCountryCode] = useState("57");
  const [name, setName] = useState("");
  const [whatsappId, setWhatsappId] = useState("");
  const [loading, setLoading] = useState(false);

  const connectedWhatsApps = useMemo(
    () => whatsApps.filter(whatsapp => whatsapp.status === "CONNECTED"),
    [whatsApps]
  );

  const normalizedTargetNumber = useMemo(() => {
    const digits = phoneDigits(number);
    if (digits.length === 10 && digits.startsWith("3")) {
      return `${phoneDigits(countryCode) || "57"}${digits}`;
    }
    return digits;
  }, [number, countryCode]);

  const targetConnection = useMemo(
    () =>
      connectedWhatsApps.find(
        whatsapp =>
          phoneDigits(whatsapp.phoneNumber) === normalizedTargetNumber
      ),
    [connectedWhatsApps, normalizedTargetNumber]
  );

  const selectableWhatsApps = useMemo(
    () =>
      connectedWhatsApps.filter(
        whatsapp => whatsapp.id !== targetConnection?.id
      ),
    [connectedWhatsApps, targetConnection]
  );

  useEffect(() => {
    if (!open) return;

    setNumber(initialNumber || "");
    setCountryCode("57");
    setName("");
  }, [open, initialNumber]);

  useEffect(() => {
    if (!open) return;

    const preferred =
      selectableWhatsApps.find(whatsapp => whatsapp.isDefault) ||
      selectableWhatsApps[0];
    setWhatsappId(current =>
      selectableWhatsApps.some(whatsapp => whatsapp.id === current)
        ? current
        : preferred?.id || ""
    );
  }, [open, selectableWhatsApps]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(phoneDigits(number));
      toast.success(i18n.t("phoneConversation.copied"));
    } catch (err) {
      toast.error(i18n.t("phoneConversation.copyError"));
    }
  };

  const handleStart = async () => {
    if (!number.trim() || !whatsappId) return;

    setLoading(true);
    try {
      const { data: ticket } = await api.post("/tickets/from-number", {
        number,
        countryCode,
        name,
        whatsappId,
      });
      onClose();
      history.push(`/tickets/${ticket.id}`);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{i18n.t("phoneConversation.title")}</DialogTitle>
      <DialogContent dividers>
        <Typography color="textSecondary" paragraph>
          {i18n.t("phoneConversation.help")}
        </Typography>
        <TextField
          label={i18n.t("phoneConversation.number")}
          value={number}
          onChange={event => setNumber(event.target.value)}
          margin="dense"
          variant="outlined"
          fullWidth
          autoFocus
        />
        <TextField
          label={i18n.t("phoneConversation.countryCode")}
          value={countryCode}
          onChange={event => setCountryCode(event.target.value)}
          margin="dense"
          variant="outlined"
          fullWidth
          helperText={i18n.t("phoneConversation.countryHelp")}
        />
        <TextField
          label={i18n.t("phoneConversation.name")}
          value={name}
          onChange={event => setName(event.target.value)}
          margin="dense"
          variant="outlined"
          fullWidth
        />
        <FormControl variant="outlined" margin="dense" fullWidth>
          <InputLabel>{i18n.t("phoneConversation.connection")}</InputLabel>
          <Select
            value={whatsappId}
            onChange={event => setWhatsappId(event.target.value)}
            label={i18n.t("phoneConversation.connection")}
          >
            {selectableWhatsApps.map(whatsapp => (
              <MenuItem key={whatsapp.id} value={whatsapp.id}>
                {whatsapp.name}
                {whatsapp.phoneNumber ? ` — ${whatsapp.phoneNumber}` : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {!selectableWhatsApps.length && (
          <Typography color="error">
            {i18n.t("phoneConversation.noConnections")}
          </Typography>
        )}
        <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 12 }}>
          {i18n.t("phoneConversation.noAutomaticMessage")}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCopy}
          color="primary"
          startIcon={<FileCopyOutlinedIcon />}
          disabled={!phoneDigits(number)}
        >
          {i18n.t("phoneConversation.copy")}
        </Button>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {i18n.t("phoneConversation.cancel")}
        </Button>
        <ButtonWithSpinner
          onClick={handleStart}
          color="primary"
          variant="contained"
          loading={loading}
          disabled={!number.trim() || !whatsappId}
        >
          {i18n.t("phoneConversation.start")}
        </ButtonWithSpinner>
      </DialogActions>
    </Dialog>
  );
};

export default StartConversationFromNumberModal;
